import { DateTimeManager } from '@components/DateTimeManager/DateTimeManager.js';
export function stringContains(compare, filter) {
    return compare.toLowerCase().trim().indexOf(filter.trim().toLowerCase()) > -1;
}
export function arrayContains(compare, filter) {
    return compare.some(item => item.toLowerCase() === filter.toLowerCase());
}
export function stringEquals(compare, filter) {
    return compare.toLowerCase() === filter.toLowerCase();
}
export function numericFilter(compare, filter) {
    // Handle range filters like "1..5"
    const rangeRegex = /^(-?\d+(\.\d+)?)\.\.(-?\d+(\.\d+)?)/;
    const rangeMatch = filter.match(rangeRegex);
    if (rangeMatch) {
        const lowerBound = parseFloat(rangeMatch[1]);
        const upperBound = parseFloat(rangeMatch[3]);
        return compare >= lowerBound && compare <= upperBound;
    }
    // Handle comparison operators: >=, <=, >, <, =
    const operatorRegex = /^(>=|<=|>|<|=)?\s*(-?\d+(\.\d+)?)$/;
    const operatorMatch = filter.match(operatorRegex);
    if (operatorMatch) {
        const operator = operatorMatch[1] || '=';
        const number = parseFloat(operatorMatch[2]);
        switch (operator) {
            case '>':
                return compare > number;
            case '<':
                return compare < number;
            case '>=':
                return compare >= number;
            case '<=':
                return compare <= number;
            case '=':
                return compare === number;
            default:
                // If no operator is provided, default to equality
                return compare === number;
        }
    }
    // If the filter is a standalone number, treat it as equality
    const standaloneNumber = parseFloat(filter);
    if (!isNaN(standaloneNumber)) {
        return compare === standaloneNumber;
    }
    // the filter format is unrecognized
    console.warn(`Unrecognized numeric filter format: "${filter}"`);
    return true;
}
export function dateFilter(dateValue, filterValue) {
    /**
     * Parses a date string and returns a Date object.
     * Ignores the time portion if present.
     * Returns null if the date string is invalid.
     *
     * @param dateStr - The date string to parse.
     * @returns A Date object or null.
     */
    function parseDate(dateStr) {
        // Extract the date part by splitting at ' ' (space) and taking the first part
        const dateOnly = dateStr.split(' ')[0];
        const date = new Date(dateOnly);
        return isNaN(date.getTime()) ? null : date;
    }
    // Parse the dateValue
    const parsedDateValue = parseDate(dateValue);
    if (!parsedDateValue) {
        console.warn(`Invalid dateValue: "${dateValue}"`);
        return false;
    }
    const [operator, filterDateStr] = extractOperatorFromString(filterValue);
    // Parse the filterDateStr
    const parsedFilterDate = parseDate(filterDateStr);
    if (!parsedFilterDate) {
        console.warn(`Invalid filterValue: "${filterValue}"`);
        return true;
    }
    // Compare the dates, ignoring the time portion
    const valueTime = parsedDateValue.getTime();
    const filterTime = parsedFilterDate.getTime();
    switch (operator) {
        case '>':
            return valueTime > filterTime;
        case '>=':
            return valueTime >= filterTime;
        case '<':
            return valueTime < filterTime;
        case '<=':
            return valueTime <= filterTime;
        case '=':
            return parsedDateValue.toISOString().slice(0, 10) === parsedFilterDate.toISOString().slice(0, 10);
        default:
            console.warn(`Unhandled operator: "${operator}"`);
            return true;
    }
}
export function dateTimeFilter(dateValue, filterValue) {
    function parseDate(dateStr) {
        const date = DateTimeManager.toDateTime(dateStr).toJSDate();
        return isNaN(date.getTime()) ? null : date;
    }
    // Parse the dateValue
    const parsedDateValue = parseDate(dateValue);
    if (!parsedDateValue) {
        console.warn(`Invalid dateValue: "${dateValue}"`);
        return false;
    }
    const [operator, filterDateStr] = extractOperatorFromString(filterValue);
    // Parse the filterDateStr
    const parsedFilterDate = parseDate(filterDateStr);
    if (!parsedFilterDate) {
        console.warn(`Invalid filterValue: "${filterValue}"`);
        return true;
    }
    // Compare the dates, ignoring the time portion
    const valueTime = parsedDateValue.getTime();
    const filterTime = parsedFilterDate.getTime();
    switch (operator) {
        case '>':
            return valueTime > filterTime;
        case '>=':
            return valueTime >= filterTime;
        case '<':
            return valueTime < filterTime;
        case '<=':
            return valueTime <= filterTime;
        case '=':
            // only care about hour and minute, not seconds etc.
            return parsedDateValue.toISOString().slice(0, 16) === parsedFilterDate.toISOString().slice(0, 16);
        default:
            console.warn(`Unhandled operator: "${operator}"`);
            return true;
    }
}
export function extractOperatorFromString(value) {
    if (!value) {
        return ['=', ''];
    }
    // Define possible operators in order of length to prevent misparsing (>= before >)
    const operators = ['>=', '<=', '>', '<', '='];
    let operator = '=';
    // Determine if the filterValue starts with any operator
    for (const op of operators) {
        if (value.startsWith(op)) {
            operator = op;
            value = value.slice(op.length).trim();
            break;
        }
    }
    return [
        operator,
        value
    ];
}
